import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout.js';
import { Clause, List, Mono, P, Plain, Terms } from './prose.js';

const EFFECTIVE = '2026-08-05';

/**
 * The terms of service. Written for the customer who signs them and for the
 * platform reviewer who checks that the app tells its users what it will refuse
 * to do — which is clause 5, and the reason this document exists in this form.
 */
export function TermsPage() {
  return (
    <LegalLayout
      label="Terms"
      title="Terms of service"
      effective={EFFECTIVE}
      standfirst={
        <>
          The agreement between Legate and the business that uses it. It states what the service
          does, what the customer is answerable for, and the instructions Legate will not follow
          however they are phrased.
        </>
      }
    >
      <Clause n={1} title="The service">
        <P>
          Legate is software for finding companies worth contacting, predicting which of them a
          message will actually move, and conducting the resulting conversations inside the channels
          Meta permits — Messenger, Instagram Direct, WhatsApp with a recorded opt-in, and email.
        </P>
        <P>
          Connections are made through Meta&rsquo;s own OAuth screen and the official Graph API.
          Legate sends and receives only on accounts the customer connected, and only within the
          permissions Meta granted for them.
        </P>
        <P>
          The agent drafts and, at the customer&rsquo;s chosen level of autonomy, sends. A policy
          layer sits above the model and can block a message the model has already produced. Every
          unsupervised action — sent or suppressed — is written to an append-only ledger with its
          reason.
        </P>
      </Clause>

      <Clause n={2} title="Accounts and connections">
        <List
          items={[
            'The customer must be entitled to connect each account: an admin of the Page, or acting under the written authority of one. Connecting an account you do not administer is a breach of these terms and of Meta’s.',
            'Credentials are personal. The customer is responsible for what its operators do with them and for removing access when someone leaves.',
            'A connection can be revoked at any time from the channels screen, or by removing the app in Meta’s settings. Either route erases the stored token, unsubscribes the webhooks and pauses anything running through it.',
            'Legate answers Meta’s deauthorize and data-deletion callbacks. When Meta tells us a person has removed the app or asked for erasure, we act on it without waiting for anyone to confirm.',
          ]}
        />
      </Clause>

      <Clause n={3} title="Acceptable use">
        <P>The customer will not use Legate to:</P>
        <List
          items={[
            'send messages that are unlawful, deceptive, threatening, or that impersonate another business or person;',
            'sell or promote anything a platform prohibits — among them adult services, illicit drugs, weapons, counterfeit goods, and unlicensed financial or medical products;',
            'contact anyone who has objected. The opt-out registry is checked before every send and is not an advisory list;',
            'reverse-engineer the service, resell access to it as a shell for automation it forbids, or hand credentials to a third party;',
            'load personal data the customer had no right to collect, or use Legate to reach consumers where the message would breach GDPR, ePrivacy, CAN-SPAM or their local equivalents.',
          ]}
        />
      </Clause>

      <Clause n={4} title="Your catalogue, your offers, your claims">
        <P>
          Everything the agent says about the customer&rsquo;s business comes from what the customer
          gave it: the offering, prices, case studies, constraints, and the material behind them.
          Legate does not verify any of it.
        </P>
        <Plain>
          The customer is solely responsible for the accuracy of its catalogue, the legality of its
          offers, the licences its trade requires, and every promise made in its name.
        </Plain>
        <P>
          If a generated message misstates a price or a term because the source material did, the
          statement is still the customer&rsquo;s. This is why every campaign starts in observation,
          where a person approves the first messages before anything is sent unsupervised.
        </P>
      </Clause>

      <Clause n={5} title="What we will not do, whoever asks">
        <P>
          These are refused in the code, not by policy. A request to enable them is not a feature
          request and will not be met.
        </P>
        <List
          items={[
            <>
              <strong className="text-ink font-medium">Unsolicited direct messages.</strong> On
              Instagram and Messenger a business cannot start a conversation; the person does. Legate
              will not simulate an opening. First contact belongs to paid click-to-message ads, to
              WhatsApp templates with a recorded opt-in, and to email.
            </>,
            <>
              <strong className="text-ink font-medium">Messaging outside the window.</strong> The
              24-hour standard messaging window is enforced before a send is attempted. The single
              permitted extension is the tag for handing a conversation to a human agent, and it is
              never used for marketing.
            </>,
            <>
              <strong className="text-ink font-medium">Personal-account automation.</strong> No
              logging into anyone&rsquo;s account, no client emulation, no unofficial libraries, no
              scraping around the Platform Terms. A customer cannot consent on Meta&rsquo;s behalf.
            </>,
            <>
              <strong className="text-ink font-medium">Buying or scraping employee data.</strong>{' '}
              Role-based public contacts only.
            </>,
            <>
              <strong className="text-ink font-medium">Hiding that the agent is an agent</strong>{' '}
              where disclosure is required or where the person asks.
            </>,
            <>
              <strong className="text-ink font-medium">Claiming a relationship that does not
              exist.</strong> Neither we nor a customer using Legate may describe the product as
              endorsed by, partnered with, or approved by Meta. The policy layer blocks those phrases
              in generated copy.
            </>,
          ]}
        />
      </Clause>

      <Clause n={6} title="Credits and payment">
        <Terms
          items={[
            {
              term: 'What a credit is',
              detail:
                'A unit of system work — a discovery pass, an enrichment, a scoring run, a generated message — denominated in US dollars and priced per operation on the pricing table. Credits are never sold per seat.',
            },
            {
              term: 'Paying in $LEGATE',
              detail:
                'Optional, and applies a discount priced from a Uniswap V3 time-weighted average over a window of no less than 30 minutes, enforced by the contract rather than by this application.',
            },
            {
              term: 'The token',
              detail:
                'Access and payment. It is not an investment, carries no promise of income, no revenue share and no claim on the operating entity. Staking unlocks product limits and pays no yield.',
            },
            {
              term: 'Refunds',
              detail:
                'Unspent credits are refundable within 14 days of purchase. Credits already consumed are not: the work they paid for was done.',
            },
            {
              term: 'Failure and retries',
              detail:
                'Work that fails for a reason inside Legate is not billed. Work refused by the policy layer is not billed either — refusing is the product working correctly.',
            },
          ]}
        />
      </Clause>

      <Clause n={7} title="Suspension">
        <P>
          Access may be suspended immediately where a platform requires it, where use breaches
          clause 3 or 5, or where a complaint rate threatens the customer&rsquo;s own channels. A
          suspension is explained in writing with what has to change.
        </P>
        <P>
          Legate also pauses campaigns on its own initiative — when a WhatsApp number&rsquo;s quality
          rating falls, when a complaint threshold is crossed, when a connection is revoked. That is
          protection of the asset, not a penalty, and it is always visible in the dashboard with its
          reason.
        </P>
      </Clause>

      <Clause n={8} title="Availability, and what is not promised">
        <P>
          Legate depends on Meta&rsquo;s APIs. Rate limits, permission reviews, policy changes and
          outages on their side affect the service and are outside our control. No guarantee is made
          about reply rates, deal volume, revenue, or the outcome of any Meta review.
        </P>
        <P>
          The service is provided without warranties beyond those the law does not permit us to
          exclude. Aggregate liability in any 12-month period is limited to the fees paid in that
          period. Neither party limits liability for death or personal injury, fraud, or anything
          else a court will not allow to be limited.
        </P>
      </Clause>

      <Clause n={9} title="Data protection">
        <P>
          For conversations conducted through connected accounts, the customer is the controller and
          Legate the processor, acting on documented instructions. The processing terms — categories,
          sub-processors, retention, the deletion route — are in the{' '}
          <Link
            to="/privacy"
            className="text-accent hover:text-accent-hover underline underline-offset-4 transition-colors duration-200"
          >
            privacy policy
          </Link>
          , which forms part of this agreement.
        </P>
      </Clause>

      <Clause n={10} title="Legate is not affiliated with Meta">
        <Plain>
          Legate is an independent product. It is built on the official Meta APIs, and that is the
          whole of the relationship.
        </Plain>
        <P>
          Legate is not affiliated with, endorsed by, sponsored by, partnered with or approved by
          Meta Platforms, Inc. or any of its companies. Facebook, Instagram, Messenger and WhatsApp
          are trademarks of Meta Platforms, Inc. Use of the Meta platforms is additionally governed
          by Meta&rsquo;s own terms and policies, which the customer accepts directly with Meta and
          which prevail over anything in this document that conflicts with them.
        </P>
      </Clause>

      <Clause n={11} title="Changes, notices and law">
        <P>
          A material change is published here with a new date in force at least 14 days beforehand.
          Continuing to use the service after that date accepts it; a customer who does not accept
          may terminate and be refunded any unspent credits.
        </P>
        <P>
          Notices to us: <Mono>legal@legate.dev</Mono>. Support: <Mono>support@legate.dev</Mono>.
          This agreement is governed by the laws of the European Union member state in which the
          operating entity is registered, and disputes go to the courts of that place.
        </P>
      </Clause>
    </LegalLayout>
  );
}

export default TermsPage;
